import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router';
import { useAppUpdate } from '../lib/app-update';
import { useAuthState } from '../lib/auth';
import { cn } from '../lib/utils';
import { House, ListOrdered, Plus, Users, WalletCards } from 'lucide-react';
import { FullPageMessage } from './FullPageMessage';

// docs/DESIGN.md §4: Home, Activity, Add (raised, center), Accounts, Family.
const NAV = [
  { to: '/', key: 'nav.home', Icon: House },
  { to: '/activity', key: 'nav.activity', Icon: ListOrdered },
  { to: '/add', key: 'nav.add', Icon: Plus, raised: true },
  { to: '/accounts', key: 'nav.accounts', Icon: WalletCards },
  { to: '/family', key: 'nav.family', Icon: Users },
] as const;

/** Signed-in shell: content plus bottom navigation on phones, top navigation on wider screens. */
export function AppLayout() {
  const { t } = useTranslation();
  const auth = useAuthState();
  const location = useLocation();
  const updateReady = useAppUpdate();

  if (auth === 'restoring') return <FullPageMessage>{t('app.loading')}</FullPageMessage>;
  if (auth === 'signedOut')
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  return (
    <div className="min-h-screen bg-bg text-fg">
      <main className="mx-auto max-w-[480px] px-5 pt-6 pb-[calc(5rem+env(safe-area-inset-bottom))]">
        {updateReady && (
          <div
            role="status"
            className="mb-4 flex items-center justify-between gap-3 rounded-[20px] border border-line bg-surface p-3 ps-4"
          >
            <span className="text-[13px]">{t('app.updateReady')}</span>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="min-h-11 shrink-0 rounded-2xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              {t('app.updateNow')}
            </button>
          </div>
        )}
        <Outlet />
      </main>

      <nav
        aria-label={t('nav.label')}
        className="fixed inset-x-0 bottom-0 z-10 border-t border-line bg-nav pb-[env(safe-area-inset-bottom)] shadow-[0_-1px_8px_rgba(29,27,24,0.04)] dark:shadow-none"
      >
        <ul className="mx-auto grid max-w-[480px] grid-cols-5">
          {NAV.map((item) => (
            <li key={item.to} className="flex justify-center">
              {'raised' in item ? (
                <NavLink
                  to={item.to}
                  className="flex min-h-16 flex-col items-center justify-start gap-1 text-[11px] font-medium text-muted focus-visible:outline-none [&.active]:font-semibold [&.active]:text-fg"
                >
                  <span className="-mt-[18px] flex size-14 items-center justify-center rounded-[18px] bg-accent text-on-accent shadow-[0_4px_12px_rgba(29,27,24,0.12)] hover:bg-accent-hover dark:shadow-none [a:focus-visible_&]:outline-2 [a:focus-visible_&]:outline-offset-2 [a:focus-visible_&]:outline-accent">
                    <item.Icon aria-hidden="true" className="size-7" strokeWidth={2.25} />
                  </span>
                  {t(item.key)}
                </NavLink>
              ) : (
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-16 w-full flex-col items-center justify-center gap-1 text-[11px] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
                      isActive ? 'font-semibold text-fg' : 'font-medium text-muted',
                    )
                  }
                >
                  <item.Icon aria-hidden="true" className="size-[22px]" strokeWidth={2} />
                  {t(item.key)}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
